import type { ReactNode } from 'react';
import { useGo } from '../../app/navigation';
import { openMicSetup } from '../../app/uiState';
import { APP_VERSION, CURRENT_RELEASE } from '../../content/changelog';
import { audio } from '../../core/audio';
import { type Settings, setSetting, useSettings } from '../../core/store';
import { noteName, pcName } from '../../core/theory';
import { fmtDate } from '../../core/util';
import { Btn } from '../../ui/Button';
import { Icon } from '../../ui/icons';
import { Rich } from '../../ui/Rich';
import { vars } from '../../ui/style';

function Seg<K extends keyof Settings>({
  k,
  options,
  label,
}: {
  k: K;
  options: [Settings[K], string][];
  label: string;
}) {
  const settings = useSettings();
  return (
    <div className="seg" role="radiogroup" aria-label={label}>
      {options.map(([val, label]) => (
        // biome-ignore lint/a11y/useSemanticElements: segmented control keeps the original button markup
        <button
          key={String(val)}
          type="button"
          role="radio"
          aria-checked={settings[k] === val}
          className={settings[k] === val ? 'on' : undefined}
          onClick={() => setSetting(k, val)}
        >
          {label}
        </button>
      ))}
    </div>
  );
}

function Row({ title, desc, children }: { title: string; desc?: ReactNode; children: ReactNode }) {
  return (
    <div className="set-row">
      <div>
        <div className="sr-t">{title}</div>
        {desc ? <div className="sr-d">{typeof desc === 'string' ? <Rich text={desc} /> : desc}</div> : null}
      </div>
      {children}
    </div>
  );
}

export function SettingsPage() {
  const go = useGo();
  const s = useSettings();
  const sample = `${pcName(7)}, ${pcName(10)}, ${noteName(57)}`;
  const released = CURRENT_RELEASE?.date ? `Yayın tarihi: ${fmtDate(CURRENT_RELEASE.date)}. ` : '';
  return (
    <div style={vars({ '--mod': 'var(--accent)' })}>
      <header className="mod-head" style={{ marginBottom: '22px' }}>
        <div className="mod-icon">
          <Icon name="sliders" />
        </div>
        <div className="mod-titles">
          <h1>Ayarlar</h1>
          <p>Nota adları, ses, mikrofon ve görünüm.</p>
        </div>
      </header>
      <div className="settings">
        <h2 className="sec-title">Nota adları</h2>
        <Row
          title="Adlandırma"
          desc={
            <>
              Örnek: <span className="tok-note">{sample}</span>
            </>
          }
        >
          <Seg
            k="noteNames"
            label="Adlandırma"
            options={[
              ['both', 'Do (C)'],
              ['solfege', 'Do Re Mi'],
              ['letter', 'C D E'],
            ]}
          />
        </Row>
        <Row
          title="Arada kalan notalar"
          desc="Siyah tuşların adı: diyez (Fa♯) ya da bemol (Sol♭). Tonalite içinde uygun olan otomatik seçilir."
        >
          <Seg
            k="accidental"
            label="Arada kalan notalar"
            options={[
              ['sharp', '♯ diyez'],
              ['flat', '♭ bemol'],
            ]}
          />
        </Row>
        <h2 className="sec-title" style={{ marginTop: '14px' }}>
          Ses
        </h2>
        <Row title="Çalgı" desc="Antrenmanlarda notaları çalan ses. Vokal egzersizleri gitar yerine piyano kullanır.">
          <Seg
            k="instrument"
            label="Çalgı"
            options={[
              ['guitar', 'Gitar'],
              ['piano', 'Piyano'],
              ['sine', 'Saf ton'],
            ]}
          />
        </Row>
        <Row title="Ses düzeyi">
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center', minWidth: '220px' }}>
            <input
              id="set-volume"
              aria-label="Ses düzeyi"
              type="range"
              min="0"
              max="1"
              step="0.05"
              value={s.volume}
              onChange={(e) => setSetting('volume', Number.parseFloat(e.target.value))}
            />
            <Btn
              kind="ghost"
              icon="play"
              onClick={async () => {
                await audio.resume();
                audio.stopAll();
                audio.playNotes([60, 64, 67, 72], { bpm: 140 });
              }}
            >
              Örnek çal
            </Btn>
          </div>
        </Row>
        <Row title="Referans La (A4)" desc="Akort aleti ve bütün sesler bu frekansa göre ayarlanır.">
          <select
            id="set-a4"
            aria-label="Referans La (A4)"
            value={String(s.a4)}
            onChange={(e) => setSetting('a4', Number.parseInt(e.target.value, 10))}
          >
            {[432, 435, 438, 440, 442, 444].map((v) => (
              <option key={v} value={String(v)}>
                {v} Hz
              </option>
            ))}
          </select>
        </Row>
        <Row title="Tonaliteyi duyur" desc="Uzun melodilerden önce tonalitenin akorunu çal.">
          <input
            id="set-keyhint"
            aria-label="Tonaliteyi duyur"
            type="checkbox"
            checked={s.showKeyHint}
            onChange={(e) => setSetting('showKeyHint', e.target.checked)}
          />
        </Row>
        <h2 className="sec-title" style={{ marginTop: '14px' }}>
          Mikrofon ve değerlendirme
        </h2>
        <Row
          title="Oktav"
          desc="Esnekte doğru nota farklı oktavda çalınsa da kabul edilir. Kesinde aynı yükseklik gerekir."
        >
          <Seg
            k="octaveMode"
            label="Oktav"
            options={[
              ['flex', 'Esnek'],
              ['strict', 'Kesin'],
            ]}
          />
        </Row>
        <Row title="Kulaklık kullanıyorum" desc="Açıkken uygulama ses çalarken de mikrofon dinlemeye devam eder.">
          <input
            id="set-hp"
            aria-label="Kulaklık kullanıyorum"
            type="checkbox"
            checked={s.headphones}
            onChange={(e) => setSetting('headphones', e.target.checked)}
          />
        </Row>
        <Row title="Mikrofon" desc="Cihaz seçimi, hassasiyet ve canlı test.">
          <Btn kind="ghost" icon="mic" onClick={openMicSetup}>
            Mikrofon ayarları
          </Btn>
        </Row>
        <Row
          title="Ses aralığı"
          desc={`Vokal egzersizleri bu aralığa göre ayarlanır (${
            s.voiceLow != null && s.voiceHigh != null
              ? `şu an ${noteName(s.voiceLow)} – ${noteName(s.voiceHigh)}`
              : 'henüz belirlenmedi'
          }).`}
        >
          <Btn kind="ghost" icon="voice" onClick={() => go('vokal.aralik-testi')}>
            Testi aç
          </Btn>
        </Row>
        <h2 className="sec-title" style={{ marginTop: '14px' }}>
          Görünüm ve hedef
        </h2>
        <Row title="Tema" desc="Sistem, cihazının açık/koyu ayarını izler.">
          <Seg
            k="theme"
            label="Tema"
            options={[
              ['system', 'Sistem'],
              ['light', 'Açık'],
              ['dark', 'Koyu'],
            ]}
          />
        </Row>
        <Row title="Günlük hedef" desc="Ana sayfadaki hedef halkası.">
          <select
            id="set-goal"
            aria-label="Günlük hedef"
            value={String(s.dailyGoal)}
            onChange={(e) => setSetting('dailyGoal', Number.parseInt(e.target.value, 10))}
          >
            {[30, 60, 100, 150].map((v) => (
              <option key={v} value={String(v)}>
                {v} XP
              </option>
            ))}
          </select>
        </Row>
        <h2 className="sec-title" style={{ marginTop: '14px' }}>
          Hakkında
        </h2>
        <Row
          title={`Sürüm ${APP_VERSION}`}
          desc={`${released}Her sürümde neyin değiştiğini Yenilikler sayfasında okuyabilirsin.`}
        >
          <Btn kind="ghost" icon="spark" onClick={() => go('yenilikler')}>
            Yenilikler
          </Btn>
        </Row>
        <p className="mb-note" style={{ marginTop: '10px' }}>
          Ayarlar ve ilerleme yalnızca bu tarayıcıda saklanır. Mikrofon sesi kaydedilmez ve hiçbir yere gönderilmez.
        </p>
      </div>
    </div>
  );
}
